import React, { useEffect, useState } from 'react';
import { Box, Button, Stack } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { useTranslation } from 'react-i18next';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import { FolderIcon } from '@components/icons/svg-icon.tsx';
import Loader from '@components/loader/loader.tsx';

interface Folder {
  id: string;
  name: string;
  children?: Folder[];
}

interface MoveTreeContentProps {
  onMove: (targetId: string) => void;
}

const MoveTreeContent: React.FC<MoveTreeContentProps> = ({ onMove }) => {
  const {
    moveTreeData,
    getMoveTreeData,
    currentFolder,
    isFolderLoading,
    isMoveTreeLoading,
    checkedItems
  } = useMediaLibraryStore();
  const { t } = useTranslation();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const selectedFolderIds = Object.keys(checkedItems || {}).filter(
    (id) => checkedItems[id]
  );

  useEffect(() => {
    getMoveTreeData();
  }, [getMoveTreeData]);

  const findPathToId = (
    nodes: Folder[] | undefined,
    targetId: string
  ): string[] | null => {
    if (!nodes) return null;

    for (const node of nodes) {
      if (node.id === targetId) return [node.id];

      const childPath = findPathToId(node.children, targetId);
      if (childPath) return [node.id, ...childPath];
    }

    return null;
  };

  useEffect(() => {
    if (currentFolder?.id) {
      const path = findPathToId(moveTreeData, currentFolder.id);
      if (path) {
        setExpandedItems(path);
      }
    }
  }, [moveTreeData, currentFolder?.id]);

  const renderTree = (nodes: Folder[] | undefined): React.ReactNode =>
    nodes?.map((node) => {
      const isDisabled = selectedFolderIds.includes(node.id);

      return (
        <TreeItem
          key={node.id}
          itemId={node.id}
          disabled={isDisabled}
          label={
            <Stack direction="row" alignItems="center" spacing={1}>
              <FolderIcon fontSize="small" />
              <span
                className={isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
              >
                {node.name}
              </span>
            </Stack>
          }
        >
          {node.children &&
            node.children.length > 0 &&
            renderTree(node.children)}
        </TreeItem>
      );
    });

  return (
    <Box className="flex flex-col justify-between" gap={3}>
      <div className="bg-[--white] px-5 py-3 border-1 border-solid border-[rgba(213,215,229,1)] rounded-[13px]">
        {isMoveTreeLoading() ? (
          <div className="flex justify-center items-center min-h-[80px]">
            <Loader />
          </div>
        ) : (
          <SimpleTreeView
            selectedItems={selectedId ?? ''}
            expandedItems={expandedItems}
            onExpandedItemsChange={(event, newExpanded) => {
              setExpandedItems(newExpanded);
            }}
            onSelectedItemsChange={(event, newSelectedItem) => {
              setSelectedId(newSelectedItem || null);
            }}
          >
            {renderTree(moveTreeData)}
          </SimpleTreeView>
        )}
      </div>

      <div className="flex justify-end pt-4 pb-1 sticky bottom-0 bg-white">
        <Button
          type="submit"
          variant="contained"
          loading={isFolderLoading()}
          disabled={!selectedId}
          onClick={() => selectedId && onMove?.(selectedId)}
        >
          {t('save')}
        </Button>
      </div>
    </Box>
  );
};

export default MoveTreeContent;
